import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, Users, GraduationCap, BookOpen, DollarSign,
  Award, RefreshCw, Building2, ChevronUp, ChevronDown, Target
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#0F2C59', '#DAA520', '#3B82F6', '#10B981', '#EF4444', '#8B5CF6', '#F59E0B', '#EC4899'];
const ROLE_LABELS = {
  student: 'Etudiants', teacher: 'Enseignants', visiting_teacher: 'Vacataires',
  dean: 'Doyens', rector: 'Recteurs', admin_staff: 'Personnel'
};

export const AnalyticsPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/analytics/overview`);
      setData(res.data);
    } catch { toast.error('Erreur chargement analytics'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <LoadingSkel />;
  if (!data) return <div className="text-center py-12 text-gray-500">Donnees non disponibles</div>;

  const roleData = Object.entries(data.role_distribution || {})
    .map(([role, count]) => ({ name: ROLE_LABELS[role] || role, value: count }))
    .filter(d => d.value > 0);

  return (
    <div className="space-y-6 animate-slide-in" data-testid="analytics-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Analytics avances</h1>
          <p className="text-sm text-gray-500 mt-1">Statistiques detaillees et tendances</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-analytics">
          <RefreshCw className="w-4 h-4 mr-2" />Actualiser
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPICard icon={GraduationCap} label="Etudiants" value={data.total_students} color="#0F2C59" testId="kpi-students" />
        <KPICard icon={Users} label="Enseignants" value={data.total_teachers} color="#3B82F6" testId="kpi-teachers" />
        <KPICard icon={BookOpen} label="Cours" value={data.total_courses} color="#8B5CF6" testId="kpi-courses" />
        <KPICard icon={Award} label="Moy. generale" value={`${data.avg_grade}/20`} color="#DAA520" testId="kpi-avg-grade" />
        <KPICard icon={Target} label="Taux reussite" value={`${data.pass_rate}%`} color="#10B981" testId="kpi-pass-rate" />
        <KPICard icon={DollarSign} label="Revenus" value={`$${data.total_revenue?.toLocaleString()}`} color="#EF4444" testId="kpi-revenue" />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Vue generale</TabsTrigger>
          <TabsTrigger value="academic">Academique</TabsTrigger>
          <TabsTrigger value="financial">Financier</TabsTrigger>
          <TabsTrigger value="distribution">Repartition</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Enrollment Trend */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />Tendance des inscriptions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.enrollment_trend?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={data.enrollment_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#0F2C59" strokeWidth={2} dot={{ fill: '#0F2C59', r: 4 }} name="Inscriptions" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <EmptyChart label="Aucune donnee d'inscription" />}
              </CardContent>
            </Card>

            {/* Role Distribution Pie */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="w-4 h-4" />Repartition par role
                </CardTitle>
              </CardHeader>
              <CardContent>
                {roleData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={roleData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, value }) => `${name} (${value})`} labelLine={false}>
                        {roleData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <EmptyChart label="Aucune donnee" />}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ACADEMIC */}
        <TabsContent value="academic" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Faculty Distribution */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Building2 className="w-4 h-4" />Etudiants par faculte
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.faculty_distribution?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={data.faculty_distribution} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={120} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="students" fill="#0F2C59" name="Etudiants" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="teachers" fill="#DAA520" name="Enseignants" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyChart label="Aucune faculte configuree" />}
              </CardContent>
            </Card>

            {/* Academic Performance */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Award className="w-4 h-4" />Performance academique
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <MetricCard label="Moyenne generale" value={`${data.avg_grade}/20`}
                    trend={data.avg_grade >= 10 ? 'up' : 'down'} />
                  <MetricCard label="Taux de reussite" value={`${data.pass_rate}%`}
                    trend={data.pass_rate >= 50 ? 'up' : 'down'} />
                  <MetricCard label="Total des notes" value={data.total_grades} />
                  <MetricCard label="Etudiants actifs" value={data.active_students} />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* FINANCIAL */}
        <TabsContent value="financial" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Revenue Trend */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />Evolution des revenus
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.monthly_revenue?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={data.monthly_revenue}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, 'Montant']} />
                      <Bar dataKey="amount" fill="#10B981" radius={[4, 4, 0, 0]} name="Revenus ($)" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyChart label="Aucune donnee de revenu" />}
              </CardContent>
            </Card>

            {/* Revenue KPIs */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />Indicateurs financiers
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-3">
                  <div className="p-4 bg-emerald-50 rounded-md border border-emerald-200">
                    <p className="text-xs text-emerald-600 font-medium">Total collecte</p>
                    <p className="text-2xl font-bold text-emerald-800">${data.total_revenue?.toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-md border border-blue-200">
                    <p className="text-xs text-blue-600 font-medium">Revenu par etudiant</p>
                    <p className="text-2xl font-bold text-blue-800">
                      ${data.total_students > 0 ? Math.round(data.total_revenue / data.total_students).toLocaleString() : 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* DISTRIBUTION */}
        <TabsContent value="distribution" className="mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(data.role_distribution || {}).map(([role, count]) => (
              <Card key={role}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{ROLE_LABELS[role] || role}</p>
                    <p className="text-2xl font-bold">{count}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[var(--tenant-primary)]/10">
                    <Users className="w-5 h-5 text-[var(--tenant-primary)]" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const KPICard = ({ icon: Icon, label, value, color, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-3 text-center">
      <Icon className="w-5 h-5 mx-auto mb-1" style={{ color }} />
      <p className="text-lg sm:text-xl font-bold">{value}</p>
      <p className="text-[10px] text-gray-500">{label}</p>
    </CardContent>
  </Card>
);

const MetricCard = ({ label, value, trend }) => (
  <div className="p-3 bg-gray-50 rounded-md border">
    <p className="text-xs text-gray-500">{label}</p>
    <div className="flex items-center gap-1">
      <p className="text-lg font-bold">{value}</p>
      {trend === 'up' && <ChevronUp className="w-4 h-4 text-emerald-500" />}
      {trend === 'down' && <ChevronDown className="w-4 h-4 text-red-500" />}
    </div>
  </div>
);

const EmptyChart = ({ label }) => (
  <div className="h-[250px] flex items-center justify-center text-sm text-gray-400">{label}</div>
);

const LoadingSkel = () => (
  <div className="space-y-4 animate-pulse" data-testid="analytics-loading">
    <div className="h-8 bg-gray-200 rounded w-48" />
    <div className="grid grid-cols-6 gap-3">{[...Array(6)].map((_, i) => <div key={i} className="h-24 bg-gray-200 rounded" />)}</div>
    <div className="grid grid-cols-2 gap-4">{[1, 2].map(i => <div key={i} className="h-64 bg-gray-200 rounded" />)}</div>
  </div>
);

export default AnalyticsPage;
